import React, { type ReactNode } from "react";
import { css, cx, styled } from "../../../theme/styled";
import { Card } from "../../card/Card";
import { Body, Title } from "../../typography/Typography";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children: React.JSX.Element | React.JSX.Element[];
  isChecked?: boolean;
  disabled?: boolean;
}

const InputCardComponent = ({
  children,
  isChecked,
  disabled,
  ...props
}: Props): React.JSX.Element => {
  const cardVariant = isChecked ? Card.Variant.Selected : Card.Variant.Plain;

  return (
    <div
      className={cx(
        cardVariant,
        Card.Hover.ToSelected,
        disabled ? CardDisabled : CardClickable,
        frameStyles,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const frameStyles = css({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gridColumnGap: "$spacing-16",
  padding: "$spacing-16",
  lineHeight: "1.25rem",

  "&:last-of-type": {
    marginBottom: 0,
  },
});

const InputCardTitle = ({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element => {
  return (
    <CardTitle size="medium" as="span" aria-hidden>
      {children}
    </CardTitle>
  );
};

const InputCardDescription = ({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element => {
  return (
    <Body size="small" color="$text-secondary">
      {children}
    </Body>
  );
};

const CardTitle = styled(Title)({
  lineHeight: "inherit",
  marginBottom: "$spacing-2",
  fontWeight: "400",
  display: "block",
});

const CardClickable = css({
  cursor: "pointer",
});

const CardDisabled = css({
  pointerEvents: "none",
  backgroundColor: "$surface-disabled",
  borderColor: "$border-disabled",

  "&:hover": {
    borderColor: "$border-disabled",
  },

  span: {
    color: "$on-surface-disabled",
  },
});

export const InputCard = Object.assign(InputCardComponent, {
  Title: InputCardTitle,
  Description: InputCardDescription,
});
